"use client";

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { motion } from "framer-motion";

export default function TechnicalSupportEcosystem() {
  const t = useTranslations('plsyy');
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const containerVariantsLocal = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.8 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const cards = [
    {
      title: t('technicalStrengthTitle'),
      img: "/image/plsyy/about/technical.webp",
      icon: "/icon/support-1.svg",
      description: t('technicalStrengthDesc')
    },
    {
      title: t('qualityAssuranceTitle'),
      img: "/image/plsyy/about/quality.webp",
      icon: "/icon/support-2.svg",
      description: t('qualityAssuranceDesc')
    },
    {
      title: t('solutionServiceTitle'),
      img: "/image/plsyy/about/solution.webp",
      icon: "/icon/support-3.svg",
      description: t('solutionServiceDesc')
    },
    {
      title: t('afterSalesServiceTitle'),
      img: "/image/plsyy/about/afterSales.webp",
      icon: "/icon/support-4.svg",
      description: t('afterSalesServiceDesc')
    },
    {
      title: t('quickResponseServiceTitle'),
      img: "/image/plsyy/about/quick.webp",
      icon: "/icon/support-5.svg",
      description: t('quickResponseServiceDesc')
    },
    {
      title: t('globalNetworkTitle'),
      img: "/image/plsyy/about/global.webp",
      icon: "/icon/support-6.svg",
      description: t('globalNetworkDesc')
    }
  ];

  return (
    <motion.div 
      id="technical-support"
      className={clsx("my-20 max-md:my-10 scroll-mt-40")}
      variants={containerVariantsLocal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* 二级标题 */}
      <motion.div variants={titleVariants} className="px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-black leading-tight text-center">
          {t('technicalSupportEcosystemTitle')}
        </h2>
      </motion.div>

      <motion.div 
        className="flex justify-center px-4 sm:px-6"
        variants={titleVariants}
      >
        <p className="container mx-auto my-4 sm:my-5 text-sm sm:text-base xl:text-lg leading-relaxed text-[#545454]">
          {t('technicalSupportEcosystemDesc')}
        </p>
      </motion.div>

      {/* 四个图文卡片 */}
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-4 sm:mt-6 container mx-auto px-4 sm:px-6"
        variants={containerVariantsLocal}
      >
        {cards.map((card, idx) => {
          return (
            <motion.div
              key={card.title}
              className={clsx(
                "relative flex-1 flex flex-col h-[300px] sm:h-[350px] md:h-[400px] group cursor-pointer overflow-hidden"
              )}
              variants={cardVariants}
              onMouseEnter={() => setHoveredCard(idx)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* 图片区域 */}
              <div className="relative flex-1 overflow-hidden bg-black">
                <motion.img 
                  src={card.img} 
                  alt={card.title} 
                  className="w-full h-full object-cover"
                  animate={{
                    scale: hoveredCard === idx ? 1.05 : 1,
                  }}
                  transition={{ 
                    duration: 0.4, 
                    ease: "easeOut"
                  }}
                />
                <div className="absolute inset-0 bg-mainColor/10"></div>
                <motion.div 
                  className="absolute inset-0 bg-black/40"
                  animate={{
                    opacity: hoveredCard === idx ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                />
                
                {/* Hover时显示的文字内容 */}
                <motion.div 
                  className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6 text-white"
                  animate={{
                    opacity: hoveredCard === idx ? 1 : 0
                  }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  {/* 描述文字 - 从底部缓慢延伸 */}
                  <motion.div 
                    className="mb-3 sm:mb-4"
                    animate={{
                      y: hoveredCard === idx ? 0 : 50,
                      opacity: hoveredCard === idx ? 1 : 0
                    }}
                    transition={{ 
                      duration: 0.6, 
                      delay: 0.2,
                      ease: "easeOut"
                    }}
                  >
                    <p className="text-xs sm:text-sm leading-relaxed opacity-90">
                      {card.description}
                    </p>
                  </motion.div>
                </motion.div>
              </div>

              {/* 底部内容区域 */}
              <motion.div 
                className="bg-mainColor p-3 sm:p-4"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <motion.img 
                    src={card.icon} 
                    alt={card.title}
                    className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
                    animate={{
                      scale: hoveredCard === idx ? 1.1 : 1
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.h3 
                    className="text-sm sm:text-base md:text-lg font-semibold text-white/70"
                    animate={{
                      color: hoveredCard === idx ? "white" : "white/70"
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {card.title}
                  </motion.h3>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}

